import { useEffect, useRef, useState } from "react";
import { GeoLocationFilters } from "../components/GeoLocationFilters";
import { PageHeader } from "../components/PageHeader";
import {
  fetchProjectSchedules,
  fetchProjectScheduleDetail,
  createProjectSchedule,
  updateProjectSchedule,
  deleteProjectSchedule,
  fetchAppPickerRows,
  fetchZoneGroupsForPicker,
  type ProjectSchedule,
  type ProjectScheduleDetail,
  type AppPickerRow,
  type ZoneGroupOption,
} from "../api/projectInitiationApi";

// ── helpers ──────────────────────────────────────────────────────────────────

/** Title-case every word: "nagarkurnool" → "Nagarkurnool" */
function titleCase(s: string): string {
  return s.replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Schedule card ─────────────────────────────────────────────────────────────

function ScheduleCard({
  schedule,
  onEdit,
  onDelete,
  onView,
}: {
  schedule: ProjectSchedule;
  onEdit: (s: ProjectSchedule) => void;
  onDelete: (id: string) => void;
  onView: (id: string) => void;
}) {
  return (
    <div className="pi-card">
      <div className="pi-card__top">
        <div className="pi-card__info">
          <span className="pi-card__name">{schedule.name}</span>
          <span className="pi-card__date">
            📅 Planned start: <strong>{fmtDate(schedule.plannedStartDate)}</strong>
          </span>
          {schedule.notes && <span className="pi-card__notes">{schedule.notes}</span>}
        </div>
        <div className="pi-card__count">
          <span className="pi-card__count-num">{schedule.applicationCount}</span>
          <span className="pi-card__count-label">beneficiar{schedule.applicationCount !== 1 ? "ies" : "y"}</span>
        </div>
      </div>
      <div className="pi-card__actions">
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => onView(schedule.id)}>
          View
        </button>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => onEdit(schedule)}>
          Edit
        </button>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          style={{ color: "#dc2626" }}
          onClick={() => onDelete(schedule.id)}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ── Detail drawer ──────────────────────────────────────────────────────────────

function ScheduleDetailDrawer({
  detail,
  onClose,
}: {
  detail: ProjectScheduleDetail;
  onClose: () => void;
}) {
  return (
    <div className="pi-drawer">
      <div className="pi-drawer__header">
        <div>
          <div className="pi-drawer__title">{detail.name}</div>
          <div className="pi-drawer__subtitle">
            Planned start: <strong>{fmtDate(detail.plannedStartDate)}</strong>
            &nbsp;·&nbsp;{detail.applicationCount} beneficiar{detail.applicationCount !== 1 ? "ies" : "y"}
          </div>
        </div>
        <button type="button" className="pi-drawer__close" onClick={onClose}>✕</button>
      </div>
      {detail.notes && <p className="pi-drawer__notes">{detail.notes}</p>}
      <div className="table-wrap" style={{ marginTop: "0.75rem" }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Beneficiary</th>
              <th>District</th>
              <th>Mandal</th>
              <th>Village</th>
              <th>Status</th>
              <th>Start Date</th>
            </tr>
          </thead>
          <tbody>
            {detail.applications.map((a) => (
              <tr key={a.applicationId}>
                <td style={{ fontWeight: 500 }}>{a.beneficiaryName || "—"}</td>
                <td className="text-muted">{titleCase(a.district)}</td>
                <td className="text-muted">{titleCase(a.mandal)}</td>
                <td className="text-muted">{titleCase(a.village)}</td>
                <td>
                  <span className="badge badge--pending" style={{ fontSize: "0.7rem" }}>
                    {a.status?.replace(/_/g, " ")}
                  </span>
                </td>
                <td>{fmtDate(a.plannedStartDate)}</td>
              </tr>
            ))}
            {detail.applications.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", color: "#94a3b8", padding: "1rem" }}>
                  No beneficiaries assigned yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Form ────────────────────────────────────────────────────────────────────────

type FormMode = "create" | "edit";

function ScheduleForm({
  mode,
  initial,
  onSaved,
  onCancel,
}: {
  mode: FormMode;
  initial?: ProjectSchedule | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [name, setName]                         = useState(initial?.name ?? "");
  const [plannedStartDate, setPlannedStartDate] = useState(initial?.plannedStartDate ?? "");
  const [notes, setNotes]                       = useState(initial?.notes ?? "");
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState<string | null>(null);

  // Zone groups
  const [zoneGroups, setZoneGroups]         = useState<ZoneGroupOption[]>([]);
  const [zoneGroupId, setZoneGroupId]       = useState("");

  // Beneficiary picker state
  const [districtFilter, setDistrictFilter] = useState("");
  const [mandalFilter, setMandalFilter]     = useState("");
  const [villageFilter, setVillageFilter]   = useState("");
  const [searchFilter, setSearchFilter]     = useState("");
  const [pickerRows, setPickerRows]         = useState<AppPickerRow[]>([]);
  const [selectedIds, setSelectedIds]       = useState<Set<string>>(new Set());
  const [pickerLoading, setPickerLoading]   = useState(false);
  const [pickerError, setPickerError]       = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load zone groups on mount
  useEffect(() => {
    fetchZoneGroupsForPicker().then(setZoneGroups).catch(() => {/* silent */});
  }, []);

  // Pre-select existing apps when editing
  useEffect(() => {
    if (mode === "edit" && initial?.id) {
      fetchProjectScheduleDetail(initial.id)
        .then((detail) => setSelectedIds(new Set(detail.applications.map((a) => a.applicationId))))
        .catch(() => {/* silent */});
    }
  }, [mode, initial?.id]);

  // Load picker rows on filter change (debounced)
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPickerLoading(true);
      setPickerError(null);
      fetchAppPickerRows({
        district: districtFilter,
        mandal: mandalFilter,
        village: villageFilter,
        search: searchFilter,
        zoneGroupId,
        limit: 500,
      })
        .then(setPickerRows)
        .catch((e) => setPickerError(e instanceof Error ? e.message : "Failed to load"))
        .finally(() => setPickerLoading(false));
    }, 300);
  }, [districtFilter, mandalFilter, villageFilter, searchFilter, zoneGroupId]);

  // When a zone group is selected, auto-select ALL beneficiaries in it
  useEffect(() => {
    if (zoneGroupId && pickerRows.length > 0 && !pickerLoading) {
      setSelectedIds(new Set(pickerRows.map((r) => r.applicationId)));
    }
  }, [zoneGroupId, pickerRows, pickerLoading]);

  function handleZoneGroupChange(id: string) {
    setZoneGroupId(id);
    setDistrictFilter("");
    setMandalFilter("");
    setVillageFilter("");
    setSearchFilter("");
    if (!id) setSelectedIds(new Set());
  }

  function toggleAll() {
    if (selectedIds.size === pickerRows.length && pickerRows.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pickerRows.map((r) => r.applicationId)));
    }
  }

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Group zone groups by category for the dropdown
  const zoneGroupsByCategory = zoneGroups.reduce<Record<string, ZoneGroupOption[]>>((acc, g) => {
    const cat = g.categoryName || "General";
    (acc[cat] ??= []).push(g);
    return acc;
  }, {});

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError("Schedule name is required"); return; }
    if (!plannedStartDate) { setError("Planned start date is required"); return; }
    setSaving(true);
    try {
      const ids = [...selectedIds];
      if (mode === "create") {
        await createProjectSchedule({ name, plannedStartDate, notes, applicationIds: ids });
      } else if (initial?.id) {
        await updateProjectSchedule(initial.id, { name, plannedStartDate, notes, applicationIds: ids });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="pi-form" onSubmit={(e) => void handleSubmit(e)}>
      <div className="pi-form__grid">
        {/* ── Left: schedule details ── */}
        <div className="pi-form__details">
          <h3 className="pi-form__section-title">
            {mode === "create" ? "New Project Schedule" : "Edit Schedule"}
          </h3>

          <div className="pi-form__field">
            <label className="pi-form__label">Schedule name <span>*</span></label>
            <input
              className="pi-form__input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Nizamabad — Batch A"
            />
          </div>

          <div className="pi-form__field">
            <label className="pi-form__label">Planned start date <span>*</span></label>
            <input
              type="date"
              className="pi-form__input"
              value={plannedStartDate}
              onChange={(e) => setPlannedStartDate(e.target.value)}
            />
            {plannedStartDate && (
              <p className="pi-form__hint">
                ✅ Becomes <strong>Day 1</strong> of the work progress timeline for all selected beneficiaries.
              </p>
            )}
          </div>

          <div className="pi-form__field">
            <label className="pi-form__label">Notes</label>
            <textarea
              className="pi-form__input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional remarks about this schedule batch"
            />
          </div>

          <div className="pi-form__selected-badge">
            <span className="pi-form__selected-count">{selectedIds.size}</span>
            <span>beneficiar{selectedIds.size !== 1 ? "ies" : "y"} selected</span>
          </div>

          {error && <p className="pi-form__error">{error}</p>}

          <div className="pi-form__actions">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? "Saving…" : mode === "create" ? "Create Schedule" : "Save Changes"}
            </button>
            <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>

        {/* ── Right: beneficiary picker ── */}
        <div className="pi-form__picker">
          <h3 className="pi-form__section-title">Select Beneficiaries</h3>

          {/* Zone Group quick-select */}
          {zoneGroups.length > 0 && (
            <div className="pi-picker__zone-row">
              <span className="pi-picker__zone-label">📍 Select by group</span>
              <select
                className="pi-picker__zone-select"
                value={zoneGroupId}
                onChange={(e) => handleZoneGroupChange(e.target.value)}
              >
                <option value="">— Pick a zone group —</option>
                {Object.entries(zoneGroupsByCategory).map(([cat, groups]) => (
                  <optgroup key={cat} label={cat}>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {zoneGroupId && (
                <button
                  type="button"
                  className="pi-picker__zone-clear"
                  onClick={() => handleZoneGroupChange("")}
                >
                  ✕ Clear
                </button>
              )}
            </div>
          )}

          {/* Manual filters (only when no zone group selected) */}
          {!zoneGroupId && (
            <div className="pi-picker__filters">
              <GeoLocationFilters
                idPrefix="pi-picker"
                district={districtFilter}
                mandal={mandalFilter}
                village={villageFilter}
                onDistrictChange={(d) => {
                  setDistrictFilter(d);
                  setMandalFilter("");
                  setVillageFilter("");
                }}
                onMandalChange={(m) => {
                  setMandalFilter(m);
                  setVillageFilter("");
                }}
                onVillageChange={setVillageFilter}
              />
              <input
                placeholder="Search name…"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{ flex: "1 1 8rem" }}
              />
            </div>
          )}

          {/* Table */}
          <div className="pi-picker__table-wrap">
            {pickerLoading ? (
              <p className="text-muted" style={{ padding: "1rem" }}>Loading…</p>
            ) : pickerError ? (
              <p style={{ color: "#dc2626", padding: "1rem" }}>{pickerError}</p>
            ) : (
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th style={{ width: "2.5rem" }}>
                      <input
                        type="checkbox"
                        checked={pickerRows.length > 0 && selectedIds.size === pickerRows.length}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>Beneficiary</th>
                    <th>District / Mandal</th>
                    <th>Current Start</th>
                  </tr>
                </thead>
                <tbody>
                  {pickerRows.map((row) => (
                    <tr
                      key={row.applicationId}
                      className={selectedIds.has(row.applicationId) ? "pi-picker__row--selected" : ""}
                      onClick={() => toggle(row.applicationId)}
                      style={{ cursor: "pointer" }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.applicationId)}
                          onChange={() => toggle(row.applicationId)}
                        />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{row.beneficiaryName || "—"}</div>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>{row.village}</div>
                      </td>
                      <td className="text-muted" style={{ fontSize: "0.8125rem" }}>
                        {titleCase(row.district)}<br />{titleCase(row.mandal)}
                      </td>
                      <td style={{ fontSize: "0.8125rem" }}>
                        {row.plannedStartDate ? (
                          <span>
                            {fmtDate(row.plannedStartDate)}
                            {row.initiationName && (
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>{row.initiationName}</div>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted">Not set</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {pickerRows.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: "1.5rem" }}>
                        No beneficiaries found — adjust filters above
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ProjectInitiationPage() {
  const [schedules, setSchedules]     = useState<ProjectSchedule[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [mode, setMode]               = useState<"list" | "create" | "edit" | "detail">("list");
  const [editTarget, setEditTarget]   = useState<ProjectSchedule | null>(null);
  const [detailData, setDetailData]   = useState<ProjectScheduleDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function loadSchedules() {
    setLoading(true);
    setError(null);
    try {
      setSchedules(await fetchProjectSchedules());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadSchedules(); }, []);

  async function handleDelete(id: string) {
    if (!confirm("Delete this schedule? The planned start date will be cleared from all assigned beneficiaries.")) return;
    try {
      await deleteProjectSchedule(id);
      await loadSchedules();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Delete failed");
    }
  }

  async function handleView(id: string) {
    setDetailLoading(true);
    setMode("detail");
    try {
      setDetailData(await fetchProjectScheduleDetail(id));
    } catch {
      setDetailData(null);
      setMode("list");
    } finally {
      setDetailLoading(false);
    }
  }

  function handleEdit(s: ProjectSchedule) {
    setEditTarget(s);
    setMode("edit");
  }

  async function handleSaved() {
    setMode("list");
    setEditTarget(null);
    await loadSchedules();
  }

  // ── KPIs ──
  const totalBeneficiaries = schedules.reduce((s, c) => s + c.applicationCount, 0);
  const upcoming = schedules.filter((s) => s.plannedStartDate >= new Date().toISOString().slice(0, 10));

  return (
    <div className="page">
      <PageHeader
        title="Project Initiation"
        subtitle="Set planned start dates for beneficiary groups to anchor the work progress timeline"
        actions={
          mode === "list" ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => setMode("create")}
            >
              + New Schedule
            </button>
          ) : undefined
        }
      />

      {/* ── Form view ─────────────────────────────────────────────────────── */}
      {(mode === "create" || mode === "edit") && (
        <ScheduleForm
          mode={mode === "create" ? "create" : "edit"}
          initial={editTarget}
          onSaved={() => void handleSaved()}
          onCancel={() => { setMode("list"); setEditTarget(null); }}
        />
      )}

      {/* ── Detail view ───────────────────────────────────────────────────── */}
      {mode === "detail" && (
        detailLoading ? (
          <p className="text-muted" style={{ padding: "1.5rem" }}>Loading detail…</p>
        ) : detailData ? (
          <ScheduleDetailDrawer
            detail={detailData}
            onClose={() => { setMode("list"); setDetailData(null); }}
          />
        ) : null
      )}

      {/* ── List view ─────────────────────────────────────────────────────── */}
      {mode === "list" && (
        <>
          {/* KPI strip */}
          <div className="po-reg__kpis" style={{ marginBottom: "1.5rem" }}>
            <div className="po-reg__kpi">
              <span className="po-reg__kpi-num">{schedules.length}</span>
              <span className="po-reg__kpi-label">Schedules</span>
            </div>
            <div className="po-reg__kpi">
              <span className="po-reg__kpi-num" style={{ color: "#0891b2" }}>{totalBeneficiaries}</span>
              <span className="po-reg__kpi-label">Beneficiaries</span>
            </div>
            <div className="po-reg__kpi">
              <span className="po-reg__kpi-num" style={{ color: "#16a34a" }}>{upcoming.length}</span>
              <span className="po-reg__kpi-label">Upcoming</span>
            </div>
            <div className="po-reg__kpi">
              <span className="po-reg__kpi-num" style={{ color: "#7c3aed" }}>
                {schedules.length - upcoming.length}
              </span>
              <span className="po-reg__kpi-label">Past / Active</span>
            </div>
          </div>

          {loading ? (
            <p className="text-muted" style={{ padding: "1.5rem" }}>Loading schedules…</p>
          ) : error ? (
            <div className="card card--elevated" style={{ padding: "1.25rem", color: "#dc2626" }}>
              <strong>Could not load schedules:</strong> {error}
            </div>
          ) : schedules.length === 0 ? (
            <div className="card card--elevated" style={{ padding: "2rem", textAlign: "center" }}>
              <p style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>📋</p>
              <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>No schedules yet</p>
              <p className="text-muted" style={{ marginBottom: "1rem" }}>
                Create a schedule to assign a planned start date to a group of beneficiaries.
                The timeline's day count will start from that date.
              </p>
              <button type="button" className="btn btn--primary" onClick={() => setMode("create")}>
                + Create first schedule
              </button>
            </div>
          ) : (
            <div className="pi-list">
              {schedules.map((s) => (
                <ScheduleCard
                  key={s.id}
                  schedule={s}
                  onEdit={handleEdit}
                  onDelete={(id) => void handleDelete(id)}
                  onView={(id) => void handleView(id)}
                />
              ))}
            </div>
          )}

          {/* ── How it works info box ── */}
          {schedules.length > 0 && (
            <div className="pi-info-box">
              <strong>📅 How the start date feeds the timeline</strong>
              <p>
                Once a beneficiary is assigned a <em>planned start date</em>, their Work Progress tab
                shows phase calendar dates calculated from that anchor. For example, if the start
                date is {fmtDate(schedules[0]?.plannedStartDate)} and Phase 1 takes 30 days, Phase 1
                is expected to finish by {addDays(schedules[0]?.plannedStartDate ?? new Date().toISOString().slice(0,10), 30)}.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
